import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { usersDB } from '../lib/db/users';
import { schoolsDB } from '../lib/db/schools';
import { schoolCookie } from '../lib/schoolCookie';
import { trackEvent, EVENTS } from '../lib/analytics';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';

export default function Login() {
  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [school, setSchool] = useState(null);

  const { login, signup, error: authError, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  useEffect(() => {
    const urlSchoolId = searchParams.get('schoolId');
    if (urlSchoolId) schoolCookie.set(urlSchoolId);

    const schoolId = urlSchoolId || schoolCookie.get();
    if (!schoolId) return;

    schoolsDB.getSchool(schoolId).then(setSchool).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearError(); // Clear any auth errors

    try {
      if (isSignup) {
        // Sign up
        const userCredential = await signup(email, password);
        
        // Create user profile
        await usersDB.createUser(userCredential.user.uid, {
          name,
          email: userCredential.user.email,
          schoolId: schoolCookie.get() || 'kung-fu-southern-praying-mantis',
          currentBelt: 'white-belt',
          dailyGoalMinutes: 10
        });
        schoolCookie.clear();

        trackEvent(EVENTS.USER_SIGNED_UP);
        navigate('/settings');  // Navigate to settings for new users
      } else {
        // Login
        await login(email, password);
        trackEvent(EVENTS.USER_LOGGED_IN);
        navigate('/home');
      }
    } catch (err) {
      console.error('Auth error:', err);
      // Error is already set in authError by the login/signup functions
      trackEvent(EVENTS.ERROR_OCCURRED, { 
        errorType: isSignup ? 'signup' : 'login', 
        errorMessage: err.message 
      });
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner if auth is loading
  if (loading) {
    return <LoadingSpinner message={isSignup ? t('common.signup') : t('common.login')} />;
  }

  return (
    <div className="min-h-screen flex items-start justify-center bg-gradient-to-br from-primary-600 via-blue-500 to-accent-500 px-4 pt-0.5 pb-8">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 animate-fade-in">
        <div className="text-center mb-4">
          {school?.logo && (
            <img src={school.logo} alt={school.name} className="h-32 mx-auto mb-4 object-contain" />
          )}
          <h1 className="text-3xl font-bold mb-4 text-gradient">
            {t('common.app_name')}
          </h1>
          <p className="text-xl text-gray-600">🥋 Train. Progress. Excel.</p>
        </div>
        
        <button
          onClick={() => setIsSignup(!isSignup)}
          className="w-full mb-6 text-primary-600 hover:text-primary-700 py-2 text-lg font-semibold transition-colors"
        >
          {isSignup
            ? t('auth.already_have_account')
            : t('auth.need_account')
          }
        </button>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isSignup && (
            <div>
              <label className="block text-xl font-semibold mb-1 text-gray-700">
                {t('auth.name')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input text-xl"
                required={isSignup}
              />
            </div>
          )}
          
          <div>
            <label className="block text-xl font-semibold mb-1 text-gray-700">
              {t('auth.email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input text-xl"
              required
            />
          </div>
          
          <div>
            <label className="block text-xl font-semibold mb-1 text-gray-700">
              {t('auth.password')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input text-xl"
              required
              minLength={6}
            />
          </div>
          
          {/* Show auth error from context */}
          {authError && (
            <ErrorMessage 
              error={authError} 
              onRetry={() => clearError()}
            />
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full btn btn-primary py-4 rounded-2xl font-bold disabled:opacity-50 text-2xl min-h-[60px] flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105"
          >
            {loading ? '...' : isSignup ? t('common.signup') : t('common.login')}
          </button>
        </form>
        
      </div>
    </div>
  );
}
